
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { Editor, EditorContent, getMarkRange } from "@tiptap/vue-2";

import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import Code from "@tiptap/extension-code";
import Link from "@tiptap/extension-link";
import Bold from "@tiptap/extension-bold";
import Italic from "@tiptap/extension-italic";
import HardBreak from "@tiptap/extension-hard-break";
import Heading, { Level } from "@tiptap/extension-heading";
import OrderedList from "@tiptap/extension-ordered-list";
import BulletList from "@tiptap/extension-bullet-list";
import ListItem from "@tiptap/extension-list-item";
import Strike from "@tiptap/extension-strike";
import Underline from "@tiptap/extension-underline";
import History from "@tiptap/extension-history";

import pretty from "pretty";

import { IconName } from "@/lib/icons/icons";
import Icon from "@/components/UI/Icon.vue";
import Dialog from "@/components/UI/Dialog.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import CodeInput from "@/components/Reusable/CustomInputs/CodeInput/CodeInput.vue";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";

type TiptapButton = {
  name: string;
  icon: IconName;
  functionName?: string;
  level?: Level;
};

// Create a much better UX by selecting the whole link on click
const LinkThatHighlightsOnClick = Link.extend({
  addAttributes() {
    return {
      rel: {
        default: null
      },
      href: {
        default: null
      },
      title: {
        default: null
      },
      target: {
        default: this.options.HTMLAttributes.target
      },
      class: {
        default: this.options.HTMLAttributes.class
      }
    };
  },
  addProseMirrorPlugins() {
    return [
      // Inspired by Quasar Tiptap
      // https://github.com/donotebase/quasar-tiptap/blob/495e0fa2e8cecdafd592dff90ef1414cabd0f0b4/src/extentions/Link.js#L66-L78
      new Plugin({
        key: new PluginKey("eventHandler"),
        props: {
          handleClick(view, pos) {
            const { schema, doc, tr } = view.state;
            const range = getMarkRange(doc.resolve(pos), schema.marks.link);

            if (!range) return false;

            const $start = doc.resolve(range.from);
            const $end = doc.resolve(range.to);
            const transaction = tr.setSelection(
              new TextSelection($start, $end)
            );

            view.dispatch(transaction);

            return true;
          }
        }
      })
    ];
  }
});

@Component({
  components: {
    ButtonBarInput,
    CodeInput,
    TextInput,
    Icon,
    EditorContent,
    Dialog
  }
})
export default class TipTapWrapper extends Vue {
  @Prop({
    default: ""
  })
  value!: string;

  mode: "visual" | "html" = "visual";

  // @ts-ignore
  editor!: Editor = null; // Editor is set on mounted, so no risk of it being null

  editorChange = false;

  linkDialogOpen = false;
  url: null | string = null;
  openInNewTab = false;
  existingLink = false;

  get minifiedHtml() {
    // Remove line breaks from HTML as Tip Tap converts them to empty <p> tags.
    return this.value?.replace(/^\s+|\n|\r|\s+$/gm, "");
  }

  get buttons(): TiptapButton[] {
    return [
      {
        name: "bold",
        icon: "BoldIcon",
        functionName: "toggleBold"
      },
      {
        name: "italic",
        icon: "ItalicIcon",
        functionName: "toggleItalic"
      },
      {
        name: "strike",
        icon: "StrikethroughIcon",
        functionName: "toggleStrike"
      },
      {
        name: "underline",
        icon: "UnderlineIcon",
        functionName: "toggleUnderline"
      },
      {
        name: "heading",
        icon: "H1Icon",
        level: 1
      },
      {
        name: "heading",
        icon: "H2Icon",
        level: 2
      },
      {
        name: "heading",
        icon: "H3Icon",
        level: 3
      },
      {
        name: "heading",
        icon: "H4Icon",
        level: 4
      },
      {
        name: "link",
        icon: "LinkIcon"
      },
      {
        name: "bulletList",
        icon: "ListIcon",
        functionName: "toggleBulletList"
      },
      {
        name: "orderedList",
        icon: "OlListIcon",
        functionName: "toggleOrderedList"
      }
    ];
  }

  @Watch("mode")
  onModeChange() {
    if (this.mode === "visual") {
      this.editor.chain().setContent(this.minifiedHtml, true).run();
    } else {
      this.$emit("input", pretty(this.value));
    }
  }

  @Watch("value")
  onValueChange(val: string) {
    if (!this.editorChange && this.mode === "visual") {
      this.editor.chain().setContent(val, true).run();
    }

    this.editorChange = false;
  }

  mounted() {
    this.editor = new Editor({
      content: this.minifiedHtml,
      onUpdate: this.updateValue,
      autofocus: "start",
      parseOptions: {
        preserveWhitespace: "full"
      },
      extensions: [
        Document,
        Paragraph,
        Text,
        Code,
        Bold,
        Italic,
        LinkThatHighlightsOnClick.configure({
          openOnClick: false,
          HTMLAttributes: { target: null }
        }),
        HardBreak,
        Heading.configure({
          levels: [1, 2, 3, 4, 5, 6]
        }),
        OrderedList,
        BulletList,
        ListItem,
        Strike,
        Underline,
        History
      ]
    });
  }

  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  }

  updateValue() {
    this.editorChange = true;

    // check the text because getHtml always returns something even if the input is empty
    if (this.editor.getText() === "") {
      this.$emit("input", null);
      return;
    }

    this.$emit("input", this.editor.getHTML());
  }

  handleIconClick(button: TiptapButton) {
    const editorWithFocus = this.editor.chain().focus();

    if (button.name.startsWith("heading") && button.level) {
      editorWithFocus
        .toggleHeading({
          level: button.level
        })
        .run();

      return;
    }

    if (button.name === "link") {
      this.openLinkDialog();
      return;
    }

    if (button.functionName) {
      // @ts-ignore the type doesn't support passing a name. Gotta be careful not to pass an invalid function name tho
      editorWithFocus[button.functionName]().run();
    }
  }

  openLinkDialog() {
    const rangeOfText = getMarkRange(
      this.editor.state.selection.$from,
      this.editor.schema.marks.link
    );

    // range won't exist if the link is being created
    if (rangeOfText) {
      // Select all the link, otherwise we have edge cases where the user can edit parts of the link only
      // Clicks are handled by our custom Link, but not if the cursor was moved by keyboard. So do it here too
      this.editor.commands.setTextSelection({
        from: rangeOfText.from,
        to: rangeOfText.to
      });
    }

    const attributes = this.editor.getAttributes("link");

    this.url = (attributes && attributes.href) || null;
    this.existingLink = !!this.url;
    this.openInNewTab =
      attributes && attributes.target ? attributes.target === "_blank" : false;
    this.linkDialogOpen = true;
  }

  closeLinkDialog() {
    this.linkDialogOpen = false;
    this.url = null;
    this.openInNewTab = false;
    this.existingLink = false;
  }

  saveLink() {
    const editorWithFocus = this.editor.chain().focus();
    const noSelectedText = this.editor.view.state.selection.empty;
    const target = this.openInNewTab ? "_blank" : "_self";

    // tiptap will create the link without text and anything the user writes becomes the link
    // inserting the link with the same text is better UX (and is what WP does in the default editor)
    if (noSelectedText) {
      this.editor.commands.insertContent(
        `<a target="${target}" href="${this.url}" rel="noopener noreferrer nofollow">${this.url}</a>`
      );
    } else if (this.url) {
      editorWithFocus
        .setLink({
          href: this.url,
          target
        })
        .run();
    } else {
      editorWithFocus.unsetLink().run();
    }

    this.closeLinkDialog();
  }

  removeLink() {
    this.editor.chain().focus().unsetLink().run();

    this.closeLinkDialog();
  }
}
